<template>
  <div class="project-accounting">
    <!-- Invoices Accordion -->
    <div class="accordion mb-4" role="tablist">
      <AccessBlock :id="PROJECT_INVOICES_BLOCK">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.accounting-invoices
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-file-text-o mr-2"></i>{{ $t('invoices') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse
            id="accounting-invoices"
            accordion="accounting-accordion"
            role="tabpanel"
            :visible="openSectionId === PROJECT_INVOICES_BLOCK"
          >
            <div class="accordion-content px-2 py-2">
              <ProjectInvoices :project="project" />
            </div>
          </b-collapse>
        </div>
      </AccessBlock>

      <AccessBlock :id="PROJECT_INVOICE_APPROVAL_BLOCK">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.accounting-invoice-approval
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-check-square-o mr-2"></i>{{ $t('project_invoice_approval') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse
            id="accounting-invoice-approval"
            accordion="accounting-accordion"
            role="tabpanel"
            :visible="openSectionId === PROJECT_INVOICE_APPROVAL_BLOCK"
          >
            <div class="accordion-content px-2 py-2">
              <ProjectInvoiceApproval
                v-if="invoiceApprovalProject"
                :blockId="PROJECT_INVOICE_APPROVAL_BLOCK"
                :project="invoiceApprovalProject"
              />
            </div>
          </b-collapse>
        </div>
      </AccessBlock>

      <!-- Matrix-only block (see feature page projekt-angebote-rechnungen): hidden in Legacy-Authz -->
      <AccessBlock v-if="isOrganizationRoleNavigationEnabled" :id="PROJECT_OFFERS_BLOCK">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.accounting-offers
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-file-o mr-2"></i>{{ $t('offers') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse
            id="accounting-offers"
            accordion="accounting-accordion"
            role="tabpanel"
            :visible="openSectionId === PROJECT_OFFERS_BLOCK"
          >
            <div class="accordion-content px-2 py-2">
              <ProjectOffers :project="project" />
            </div>
          </b-collapse>
        </div>
      </AccessBlock>

      <ProjectAccountingLegacySection
        v-if="!isOrganizationRoleNavigationEnabled"
        :project="project"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AccessBlock from '@/components/Access/AccessBlock.vue';
import ProjectInvoices from '@/blocks/project/ProjectInvoices.vue';
import ProjectOffers from '@/blocks/project/ProjectOffers.vue';
import ProjectInvoiceApproval from '@/blocks/project/ProjectInvoiceApproval.vue';
import ProjectAccountingLegacySection from '@/views/Projects/Accounting/ProjectAccountingLegacySection';
import {
  PROJECT_INVOICE_APPROVAL_BLOCK,
  PROJECT_INVOICES_BLOCK,
  PROJECT_OFFERS_BLOCK,
} from '@/navigation/accessNodes';

/** Number of the unsaved project (ProjectEdit `_new`): it has no blocks yet. */
const NEW_PROJECT_NUMBER = '_new';

/** Blocks of the accordion in page order; the first one with a level opens by default. */
const ACCORDION_BLOCKS = [
  PROJECT_INVOICES_BLOCK,
  PROJECT_INVOICE_APPROVAL_BLOCK,
  PROJECT_OFFERS_BLOCK,
];

export default {
  components: {
    AccessBlock,
    ProjectInvoices,
    ProjectInvoiceApproval,
    ProjectOffers,
    ProjectAccountingLegacySection,
  },
  props: {
    project: { type: Object },
  },
  data() {
    return {
      PROJECT_INVOICES_BLOCK,
      PROJECT_INVOICE_APPROVAL_BLOCK,
      PROJECT_OFFERS_BLOCK,
    };
  },
  computed: {
    ...mapGetters(['getAccessLevel']),
    ...mapGetters('auth', ['isOrganizationRoleNavigationEnabled']),
    /**
     * The first accordion section the role has a level on opens by default. In Legacy-Authz every
     * level is set, so the invoices open as before.
     */
    openSectionId() {
      return ACCORDION_BLOCKS.find((blockId) => this.getAccessLevel(blockId) !== null) ?? null;
    },
    /**
     * Data of the block project-invoice-approval: the excerpt from `Project.blocks`, null when
     * not loaded or denied. The unsaved project has no blocks; the block gets the project itself
     * and its note goes with `createProject`.
     */
    invoiceApprovalProject() {
      if (this.project.number === NEW_PROJECT_NUMBER) {
        return this.project;
      }
      return this.project.blocks?.invoiceApproval ?? null;
    },
  },
};
</script>
